<template>
  <div class="kds-page" :style="pageBackgroundStyle">
    <kds-topbar
      :status-view="statusView"
      :date-mode="dateMode"
      :custom-date="customDate"
      :is-loading="isLoading"
      :is-fullscreen="isFullscreen"
      :font-scale.sync="fontScale"
      :density-settings="densitySettings"
      :visible-groups.sync="visibleGroups"
      :grouped-tickets="groupedTickets"
      :total-tickets-count="filteredTickets.length"
      @update-status-view="statusView = $event"
      @refresh="loadTickets"
      @set-last24h="setLast24hMode"
      @custom-date-change="onCustomDateChange"
      @shift-custom-date="shiftCustomDate"
      @toggle-fullscreen="toggleFullscreen"
    />

    <kds-type-filter-bar
      :filters="typeFilters"
      :active-filter="activeTypeFilter"
      @update-active-filter="setTypeFilter"
    />

    <kds-board
      :status-view="statusView"
      :grouped-tickets="groupedTickets"
      :density-settings="densitySettings"
      :visible-groups="visibleGroups"
      :changing-id="changingId"
      :font-scale="fontScale"
      @advance="(ticket) => updateStatus(ticket, ticket.nextStatus)"
      @change-status="(ticket, status) => updateStatus(ticket, status)"
      @open-details="openDetails"
    />

    <guest-order-details-dialog
      v-model="detailsDialog"
      :order="detailsOrder"
      :guest="detailsGuest"
      :changing-status-order-id="changingId"
      @change-status="onDialogChangeStatus"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasSnackbar from "@/mixins/hasSnackbar";
import KdsTopbar from "./kds/KdsTopbar.vue";
import KdsTypeFilterBar from "./kds/KdsTypeFilterBar.vue";
import KdsBoard from "./kds/KdsBoard.vue";
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import {
  DELIVERY_TYPE_META,
  NEXT_STATUS,
  ACTIVE_STATES,
  KDS_GROUPS,
  toISODate,
  groupForState,
  mixHexColors,
} from "./kds/kdsConstants";

const DENSITY_STORAGE_KEY = "kds-density-settings";
const FONT_SCALE_STORAGE_KEY = "kds-font-scale";
const VISIBLE_GROUPS_STORAGE_KEY = "kds-visible-groups";

export default {
  name: "KitchenDisplay",
  components: { KdsTopbar, KdsTypeFilterBar, KdsBoard, GuestOrderDetailsDialog },
  mixins: [hasSnackbar],
  data() {
    return {
      reportData: {},
      isLoading: false,
      activeTypeFilter: "all",
      statusView: "all",
      densitySettings: this.loadDensitySettings(),
      fontScale: this.loadFontScale(),
      visibleGroups: this.loadVisibleGroups(),
      dateMode: "last24h",
      customDate: toISODate(new Date()),
      changingId: null,
      nowTs: Date.now(),
      isFullscreen: false,
      refreshTimer: null,
      elapsedTimer: null,
      detailsDialog: false,
      detailsOrder: null,
      detailsGuest: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    adminRestaurantIds() {
      if (this.user?.type !== "admin" || !this.user?.restaurants) return "";
      return this.user.restaurants
        .filter((item) => item.settings && item.settings.lice_bez_ugovora)
        .map((item) => item.id)
        .join(",");
    },
    dateRangeParams() {
      if (this.dateMode === "last24h") {
        const now = new Date();
        return {
          date: toISODate(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
          date2: toISODate(now),
        };
      }

      return { date: this.customDate, date2: this.customDate };
    },
    dateRangeKey() {
      return `${this.dateRangeParams.date}|${this.dateRangeParams.date2}`;
    },
    allTickets() {
      const tickets = [];
      const cutoffTs = this.nowTs - 24 * 60 * 60 * 1000;

      Object.values(this.reportData || {}).forEach((companyData) => {
        Object.values(companyData || {}).forEach((guests) => {
          (guests || []).forEach((guest) => {
            const order = guest.orders_guest && guest.orders_guest[0];
            if (!order || !ACTIVE_STATES.includes(order.state)) return;

            const meta = DELIVERY_TYPE_META[order.delivery_type] || DELIVERY_TYPE_META[2];
            const createdAt = order.created_at
              ? new Date(order.created_at).getTime()
              : this.nowTs;

            if (this.dateMode === "last24h" && createdAt < cutoffTs) return;

            const firstName = this.toLatin(guest.name || "");
            const lastName = this.toLatin(guest.lastname || "");

            tickets.push({
              id: order.id,
              state: order.state,
              deliveryType: order.delivery_type,
              typeIcon: meta.icon,
              typeLabel: this.$t(meta.labelKey),
              guestName: `${firstName} ${lastName}`.trim(),
              guestShort: `${firstName} ${lastName.charAt(0)}${
                lastName ? "." : ""
              }`.trim(),
              createdAtTs: createdAt,
              elapsedMinutes: Math.max(0, Math.round((this.nowTs - createdAt) / 60000)),
              nextStatus: order.state === "ready" ? "delivered" : NEXT_STATUS[order.state] || null,
              items: (order.order_guest_transactions || []).map((t) => ({
                quantity: t.quantity,
                name: this.toLatin(
                  (t.food && t.food.name) || (t.meals && t.meals.name) || ""
                ),
              })),
              rawOrder: order,
              rawGuest: guest,
            });
          });
        });
      });

      tickets.sort((a, b) => a.createdAtTs - b.createdAtTs);
      return tickets;
    },
    typeFilters() {
      const counts = { all: this.allTickets.length, 0: 0, 1: 0, 2: 0 };
      this.allTickets.forEach((t) => {
        counts[t.deliveryType] = (counts[t.deliveryType] || 0) + 1;
      });

      return [
        {
          value: "all",
          label: this.$t("all"),
          icon: "mdi-view-grid-outline",
          count: counts.all,
        },
        {
          value: 0,
          label: this.$t("delivery_to_address"),
          icon: DELIVERY_TYPE_META[0].icon,
          count: counts[0] || 0,
        },
        {
          value: 1,
          label: this.$t("pickup"),
          icon: DELIVERY_TYPE_META[1].icon,
          count: counts[1] || 0,
        },
        {
          value: 2,
          label: this.$t("consumption_in_restaurant"),
          icon: DELIVERY_TYPE_META[2].icon,
          count: counts[2] || 0,
        },
      ];
    },
    filteredTickets() {
      if (this.activeTypeFilter === "all") return this.allTickets;
      return this.allTickets.filter((t) => t.deliveryType === this.activeTypeFilter);
    },
    groupedTickets() {
      const groups = { pending: [], preparing: [], ready: [], delivered: [] };
      this.filteredTickets.forEach((ticket) => {
        const key = groupForState(ticket.state);
        if (key) groups[key].push(ticket);
      });
      return groups;
    },
    pageBackgroundStyle() {
      const group = KDS_GROUPS.find((g) => g.key === this.statusView);
      if (!group) return {};
      return {
        background: mixHexColors("#14171f", group.accent, 0.16),
      };
    },
  },
  watch: {
    densitySettings: {
      handler(value) {
        try {
          localStorage.setItem(DENSITY_STORAGE_KEY, JSON.stringify(value));
        } catch (error) {
          console.error("Error saving kitchen display density settings:", error);
        }
      },
      deep: true,
    },
    fontScale(value) {
      try {
        localStorage.setItem(FONT_SCALE_STORAGE_KEY, JSON.stringify(value));
      } catch (error) {
        console.error("Error saving kitchen display font scale:", error);
      }
    },
    visibleGroups: {
      handler(value) {
        try {
          localStorage.setItem(VISIBLE_GROUPS_STORAGE_KEY, JSON.stringify(value));
        } catch (error) {
          console.error("Error saving kitchen display visible columns:", error);
        }

        if (this.statusView !== "all" && !value.includes(this.statusView)) {
          this.statusView = "all";
        }
      },
      deep: true,
    },
    dateRangeKey: {
      handler() {
        this.loadTickets();
      },
    },
  },
  mounted() {
    this.loadTickets();
    this.refreshTimer = setInterval(this.loadTickets, 30000);
    this.elapsedTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 30000);
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    clearInterval(this.elapsedTimer);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
  },
  methods: {
    loadDensitySettings() {
      const defaults = { pending: "full", preparing: "full", ready: "full", delivered: "full" };
      try {
        const stored = JSON.parse(localStorage.getItem(DENSITY_STORAGE_KEY) || "null");
        if (stored) return { ...defaults, ...stored };
      } catch (error) {
        console.error("Error reading kitchen display density settings:", error);
      }
      return defaults;
    },
    loadFontScale() {
      try {
        const stored = JSON.parse(localStorage.getItem(FONT_SCALE_STORAGE_KEY) || "null");
        if (stored) return stored;
      } catch (error) {
        console.error("Error reading kitchen display font scale:", error);
      }
      return 1;
    },
    loadVisibleGroups() {
      const defaults = KDS_GROUPS.map((group) => group.key);
      try {
        const stored = JSON.parse(localStorage.getItem(VISIBLE_GROUPS_STORAGE_KEY) || "null");
        if (Array.isArray(stored) && stored.length) {
          return stored.filter((key) => defaults.includes(key));
        }
      } catch (error) {
        console.error("Error reading kitchen display visible columns:", error);
      }
      return defaults;
    },
    setTypeFilter(value) {
      this.activeTypeFilter = value;
    },
    setLast24hMode() {
      this.dateMode = "last24h";
    },
    onCustomDateChange(value) {
      if (!value) return;
      this.customDate = value;
      this.dateMode = "custom";
    },
    shiftCustomDate(days) {
      const base = new Date(`${this.customDate}T00:00:00`);
      base.setDate(base.getDate() + days);
      this.customDate = toISODate(base);
      this.dateMode = "custom";
    },
    loadTickets() {
      if (this.user?.type === "admin" && !this.adminRestaurantIds) return;

      this.isLoading = true;

      const request =
        this.user?.type === "admin"
          ? reportService.getCompanyMultipleNoContract({
              ...this.dateRangeParams,
              restaurants: this.adminRestaurantIds,
            })
          : reportService.getNoContractDelivery(
              this.$store.state.restaurant?.id,
              this.dateRangeParams
            );

      request
        .then((response) => {
          this.reportData = response.data || {};
        })
        .catch((error) => {
          console.error("Error loading kitchen display data:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async updateStatus(ticket, newStatus) {
      if (!newStatus || ticket.state === newStatus) return;

      this.changingId = ticket.id;

      // "status" is the field the backend reads (see change-order-status usages
      // elsewhere in the app, e.g. ReportTab.vue); "state" is kept too since the
      // rest of this codebase sends both defensively.
      const payload = {
        ...ticket.rawOrder,
        order_id: ticket.rawOrder.id,
        user_id: ticket.rawGuest.id,
        status: newStatus,
        state: newStatus,
      };

      try {
        await reportService.changeOrderStatus(payload);
        ticket.rawOrder.state = newStatus;
        this.showSuccessSnackbar(this.$t("status_changed_successfully"));
        this.loadTickets();
      } catch (error) {
        console.error("Error changing order status:", error);
        this.showErrorSnackbar(this.$t("status_change_error"));
      } finally {
        this.changingId = null;
      }
    },
    openDetails(ticket) {
      this.detailsOrder = ticket.rawOrder;
      this.detailsGuest = ticket.rawGuest;
      this.detailsDialog = true;
    },
    onDialogChangeStatus({ order, guest, newStatus }) {
      this.updateStatus(
        { rawOrder: order, rawGuest: guest, id: order.id, state: order.state },
        newStatus
      );
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else {
        document.exitFullscreen().catch(() => {});
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
  },
};
</script>

<style scoped>
.kds-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: #14171f;
  overflow: hidden;
  transition: background 0.35s ease;
}
</style>
